import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/authService';
import { SUCCESS_MESSAGES, ERROR_MESSAGES } from '../constants';

// Initial state
const initialState = {
  user: null,
  company: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

// Action types
const actionTypes = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  UPDATE_USER: 'UPDATE_USER',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
        error: null,
      };
    case actionTypes.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        company: action.payload.company,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null,
      };
    case actionTypes.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        company: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload,
      };
    case actionTypes.LOGOUT:
      return {
        ...initialState,
      };
    case actionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case actionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    case actionTypes.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    default:
      return state;
  }
};

// Context
const AuthContext = createContext(null);

// Provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUserFromStorage = () => {
      try {
        // Limpar tokens antigos hardcoded para forçar novo login
        const accessToken = localStorage.getItem('access_token');
        if (accessToken && accessToken.includes('5c79c833-ea70-4066-b1b9-76b07308ecd2')) {
          // Token hardcoded detectado - limpar tudo
          localStorage.removeItem('access_token');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('company');
          console.log('🔧 Token hardcoded removido - novo login necessário');
          return;
        }
        
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        const company = localStorage.getItem('company');

        if (token && user && company) {
          const userData = JSON.parse(user);
          const companyData = JSON.parse(company);
          
          // Verificar se os dados da empresa são válidos
          if (companyData.name && companyData.name !== 'Empresa Exemplo') {
            dispatch({
              type: actionTypes.LOGIN_SUCCESS,
              payload: {
                token,
                user: userData,
                company: companyData,
              },
            });
          } else {
            // Dados antigos, limpar e forçar novo login
            localStorage.removeItem('token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            localStorage.removeItem('company');
          }
        }
      } catch (error) {
        console.error('Error loading user from storage:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('company');
      }
    };

    loadUserFromStorage();
  }, []);

  // Login function
  const login = async (email, password) => {
    dispatch({ type: actionTypes.SET_LOADING, payload: true });

    try {
      const response = await authService.login(email, password);
      
      const { access_token, refresh_token, user, company } = response.data;

      // SEMPRE limpar TODOS os dados antes de salvar novos (segurança multi-tenant)
      localStorage.clear();
      
      // Save to localStorage com dados limpos
      localStorage.setItem('token', access_token);
      localStorage.setItem('access_token', access_token); // Garantir ambas as chaves
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('company', JSON.stringify(company));

      dispatch({
        type: actionTypes.LOGIN_SUCCESS,
        payload: { token: access_token, user, company },
      });

      return { success: true, message: SUCCESS_MESSAGES.LOGIN_SUCCESS };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || ERROR_MESSAGES.SERVER_ERROR;
      
      dispatch({
        type: actionTypes.LOGIN_FAILURE,
        payload: errorMessage,
      });

      return { success: false, message: errorMessage };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // LIMPAR COMPLETAMENTE o localStorage (segurança multi-tenant)
      localStorage.clear();
      
      dispatch({ type: actionTypes.LOGOUT });
    }
  };

  // Update user function
  const updateUser = (userData) => {
    dispatch({
      type: actionTypes.UPDATE_USER,
      payload: userData,
    });
    
    // Update localStorage
    if (state.user) {
      const updatedUser = { ...state.user, ...userData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: actionTypes.CLEAR_ERROR });
  };

  // Check if user has permission
  const hasPermission = (permission) => {
    if (!state.user || !state.user.role) return false;
    
    const userPermissions = state.user.permissions || [];
    
    // Owner has all permissions
    if (state.user.role === 'owner') return true;
    
    // Check if user has specific permission or wildcard
    return userPermissions.includes(permission) || userPermissions.includes('*');
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissions) => {
    return permissions.some(permission => hasPermission(permission));
  };

  // Check if user has all specified permissions
  const hasAllPermissions = (permissions) => {
    return permissions.every(permission => hasPermission(permission));
  };

  const value = {
    ...state,
    login,
    logout,
    updateUser,
    clearError,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 